import * as React from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from 'lucide-react';
import {
  Chevron,
  DayButton,
  DayPicker,
  getDefaultClassNames,
} from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { clsx } from 'clsx';

function Calendar({
  className,
  classNames,
  buttonVariant = 'ghost',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      startMonth={new Date(1900, 0)}
      endMonth={new Date(new Date().getFullYear() + 10, 11)}
      showOutsideDays={true}
      weekStartsOn={1}
      className={cn(
        'group/calendar bg-background p-3 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout="dropdown"
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('pl-PL', { month: 'short' }),
        formatWeekdayName: (date) =>
          date.toLocaleString('pl-PL', { weekday: 'narrow' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'flex gap-4 flex-col md:flex-row relative',
          defaultClassNames.months,
        ),
        month: cn('flex flex-col w-full gap-4', defaultClassNames.month),
        nav: cn(
          'flex items-center gap-1 w-full absolute top-0 mt-[5px] justify-between',
          defaultClassNames.nav,
        ),
        month_caption: cn(
          'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size) text-neutral-700',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: clsx(
          'relative has-focus:border-ring border border-neutral-200 shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-sm',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'select-none font-medium rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-neutral-500 rounded-md flex-1 font-normal text-[0.8rem] select-none',
          defaultClassNames.weekday,
        ),
        week: cn('flex w-full mt-2', defaultClassNames.week),
        week_number_header: cn(
          'select-none w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'text-[0.8rem] select-none text-muted-foreground',
          defaultClassNames.week_number,
        ),
        day: cn(
          'relative w-full h-full p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none',
          defaultClassNames.day,
        ),
        range_start: cn(
          'rounded-l-md bg-accent',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
        today: cn(
          'rounded-md data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Chevron: CalendarChevronBtn,
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'text-neutral-9 size-(--cell-size) flex items-center justify-center text-sm',
        defaultClassNames.day,
        // Border radius
        modifiers.range_middle && 'rounded-none',
        modifiers.range_start &&
          !modifiers.range_end &&
          'rounded-l-sm rounded-r-none',
        modifiers.range_end &&
          !modifiers.range_start &&
          'rounded-l-none rounded-r-sm',
        !modifiers.range_middle &&
          !(modifiers.range_start && !modifiers.range_end) &&
          !(modifiers.range_end && !modifiers.range_start) &&
          'rounded-sm',
        // Background
        (modifiers.range_start || modifiers.range_end) && 'bg-accent-500',
        modifiers.range_middle && 'bg-accent-100',
        modifiers.selected &&
          !modifiers.range_start &&
          !modifiers.range_end &&
          !modifiers.range_middle &&
          'bg-accent-500',
        modifiers.today && !modifiers.selected && 'bg-accent-100',
        // Hover only when not selected
        !modifiers.selected && 'hover:bg-accent-200 hover:text-neutral-900',
        // Text color
        modifiers.outside ? 'text-neutral-400' : 'text-neutral-700',
        className,
      )}
      {...props}
    />
  );
}

function CalendarChevronBtn({
  className,
  orientation,
  ...props
}: React.ComponentProps<typeof Chevron>) {
  const finalClassName = clsx(
    'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-sm p-1',
  );
  if (orientation === 'left') {
    return (
      <ChevronLeftIcon
        className="rounded-sm p-1 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        {...props}
      />
    );
  }

  if (orientation === 'right') {
    return (
      <ChevronRightIcon
        className="rounded-sm p-1 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        {...props}
      />
    );
  }

  if (orientation === 'up') {
    return (
      <ChevronUpIcon
        className="size-3 text-neutral-600 hover:text-neutral-900"
        {...props}
      />
    );
  }

  return (
    <ChevronDownIcon
      className="size-3 text-neutral-600 hover:text-neutral-900"
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
